import { useState, useEffect, useCallback } from "react";
import { Loader2, Trash2, X, Sparkles, Users as UsersIcon, BarChart3, Clock, MoonStar, AlertTriangle, BookOpen, Mail, GraduationCap, Download, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { InfoPopover } from "@/components/InfoPopover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TRANSFORM_LABELS } from "./shared/constants";
import { nudgeStatusText } from "@/lib/functieCoverage";
import { formatDate, formatRelativeNl, formatDurationSec, isSilent, fmtTs } from "./shared/format";
import { LoadingRow, EmptyState, MiniStat, StatChip, DiagRow } from "./shared/ui";

// -------- Round N: status of the weekly reminder to bedrijfsleiders (own run, own block) --------
export function ReminderStatusLine() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/site-reminders/status").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  if (!s) return null;
  const result = s.last_result === "verzonden" ? `verzonden: ${s.last_sent_count}` : s.last_result;
  const text = !s.enabled
    ? "overgeslagen: geen mailinstelling"
    : s.last_run_at ? `${fmtTs(s.last_run_at)} · ${result}` : "nog niet";
  const failing = s.enabled && s.last_failed_count > 0;
  return (
    <div className="mb-6 bg-tw-surface border border-tw px-4 py-3 sm:px-5" data-testid="reminder-status">
      <p className="font-mono text-[11px] text-tw-muted break-words">
        <Mail size={12} className="inline mr-1.5 text-tw-yellow" strokeWidth={2.5} />
        Herinnering bedrijfsleiders · maandag:{" "}
        <span className={failing ? "text-tw-red" : "text-white"} data-testid="reminder-status-text">{text}</span>
      </p>
    </div>
  );
}

// -------- Round U: the quiet-colleague nudge (Monday/Tuesday), same style as the site-reminder line --------
export function NudgeStatusLine() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/inactive-nudges/status").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  if (!s) return null;
  const result = s.last_result === "verzonden" ? `verzonden: ${s.last_sent_count}` : nudgeStatusText(s);
  const text = s.enabled && s.last_run_at ? `${fmtTs(s.last_run_at)} · ${result}` : result;
  const failing = s.enabled && s.last_failed_count > 0;
  return (
    <div className="mb-6 bg-tw-surface border border-tw px-4 py-3 sm:px-5" data-testid="nudge-status">
      <p className="font-mono text-[11px] text-tw-muted break-words">
        <Mail size={12} className="inline mr-1.5 text-tw-yellow" strokeWidth={2.5} />
        Herinnering stille collega's:{" "}
        <span className={failing ? "text-tw-red" : "text-white"} data-testid="nudge-status-text">{text}</span>
      </p>
    </div>
  );
}

// -------- Round Q: which route the AI (Claude) uses; never shows a key --------
export function AiStatusLine() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/ai-status").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  if (!s) return null;
  const route = s.provider === "anthropic" ? `eigen sleutel · ${s.model}` : "Emergent";
  const n = s.fallback_count_today || 0;
  return (
    <div className="mb-6 bg-tw-surface border border-tw px-4 py-3 sm:px-5" data-testid="ai-status">
      <p className="font-mono text-[11px] text-tw-muted break-words">
        <Sparkles size={12} className="inline mr-1.5 text-tw-yellow" strokeWidth={2.5} />
        <span className="text-white" data-testid="ai-status-route">AI: {route}</span>
        {n > 0 && (
          <span className="text-tw-red" data-testid="ai-status-fallback">
            {` · ${n} keer terugval naar Emergent`}{s.last_error_type ? ` (laatste fout: ${s.last_error_type})` : ""}
          </span>
        )}
      </p>
    </div>
  );
}

// -------- Round R: how the team rates the AI follow-up questions (hidden on error) --------
export function FollowupFeedbackBlock() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/followups/feedback").then(({ data }) => setS(data)).catch(() => {});
  }, []);
  if (!s) return null;
  return (
    <div className="mb-6 bg-tw-surface border border-tw p-4 sm:p-5" data-testid="followup-feedback-block">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircleQuestion size={14} className="text-tw-yellow" strokeWidth={2.5} />
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">DOORVRAGEN</div>
      </div>
      <p className="font-mono text-[11px] text-tw-muted" data-testid="followup-feedback-counts">
        laatste 30 dagen: <span className="text-[#32D74B]">{s.good} goed</span> · <span className="text-tw-red">{s.vague} vaag</span>
      </p>
      {s.recent_vague?.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="followup-feedback-vague-list">
          {s.recent_vague.map((v, i) => (
            <li key={`${v.created_at}-${i}`} className="border-l-2 border-tw-red/60 pl-3 min-w-0" data-testid={`followup-feedback-vague-${i}`}>
              <p className="text-sm text-white leading-snug break-words">&ldquo;{v.followup_question}&rdquo;</p>
              <p className="mt-0.5 font-mono text-[10px] text-tw-muted break-words">
                {v.user_name || "—"} · {fmtTs(v.created_at)}{v.root_question ? ` · bij: ${v.root_question}` : ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -------- D1: Weekmail block (weekly admin digest status + preview + testmail) --------
export function WeekmailBlock() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null); // html string or null
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [sending, setSending] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [savingFreq, setSavingFreq] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/weekly-digest/status");
      setStatus(data);
    } catch { /* fail-soft */ }
    try {
      const { data } = await api.get("/admin/weekly-digest/settings");
      setFrequency(data.frequency);
    } catch { /* fail-soft */ }
  };
  useEffect(() => { load(); }, []);

  const changeFrequency = async (v) => {
    setSavingFreq(true);
    try {
      await api.put("/admin/weekly-digest/settings", { frequency: v });
      setFrequency(v);
      toast.success(v === "off" ? "Weekmail uitgeschakeld." : "Weekmail ingeschakeld.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingFreq(false);
    }
  };

  const showPreview = async () => {
    setLoadingPrev(true);
    try {
      const { data } = await api.get("/admin/weekly-digest/preview");
      setPreview(data.html);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingPrev(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/weekly-digest/test");
      toast.success(`Testmail verstuurd naar ${data.sent_to}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (!status) return null;

  return (
    <div className="mb-6 bg-tw-surface border border-tw p-4 sm:p-5" data-testid="weekmail-block">
      <div className="flex items-center gap-2 mb-3">
        <Mail size={14} className="text-tw-yellow" strokeWidth={2.5} />
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">WEEKMAIL · MAANDAG 08:00</div>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3 font-mono text-[11px]">
        <span data-testid="weekmail-status" className={status.enabled ? "text-tw-green" : "text-tw-muted"}>
          Status: {status.enabled ? "actief" : "geen sleutel"}
        </span>
        <span className="text-tw-muted">
          Laatst verzonden: <span className="text-white">{status.last_sent_week ? `${status.last_sent_week} · ${fmtTs(status.last_sent_at)}` : "nog niet"}</span>
        </span>
        {status.last_result && <span className="text-tw-muted">Laatste run: <span className="text-white">{status.last_result}</span></span>}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-mono text-[10px] text-tw-muted uppercase tracking-[0.15em]">Frequentie:</span>
        <Select value={frequency} onValueChange={changeFrequency} disabled={savingFreq}>
          <SelectTrigger data-testid="weekmail-frequency-select" className="h-8 w-[140px] rounded-none bg-tw-black border-tw text-white text-xs focus:ring-0 focus:border-tw-yellow disabled:opacity-50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
            <SelectItem value="weekly" className="rounded-none text-xs">Wekelijks</SelectItem>
            <SelectItem value="off" data-testid="weekmail-frequency-off" className="rounded-none text-xs">Uit</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-[11px] text-tw-muted leading-relaxed mb-3">
        Elke maandag krijgen beheerders een overzicht van open voorstellen en wachtende AI-suggesties — alleen als er iets te melden is.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={showPreview} disabled={loadingPrev} data-testid="weekmail-preview-btn"
          className="tw-press inline-flex items-center gap-1.5 px-3 h-9 border border-tw text-tw-muted hover:text-tw-yellow hover:border-tw-yellow font-mono text-[10px] uppercase tracking-[0.15em] disabled:opacity-50">
          {loadingPrev ? <Loader2 size={12} className="animate-spin" /> : <BarChart3 size={12} strokeWidth={2.5} />} Bekijk voorbeeld
        </button>
        {status.enabled && (
          <button type="button" onClick={sendTest} disabled={sending} data-testid="weekmail-test-btn"
            className="tw-press inline-flex items-center gap-1.5 px-3 h-9 bg-tw-yellow text-black font-bold uppercase tracking-[0.15em] text-[10px] disabled:opacity-50">
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} strokeWidth={2.8} />} Verstuur testmail naar mij
          </button>
        )}
      </div>
      {preview !== null && (
        <div className="mt-4" data-testid="weekmail-preview">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[9px] tracking-[0.3em] text-tw-muted uppercase">Voorbeeld</div>
            <button type="button" onClick={() => setPreview(null)} data-testid="weekmail-preview-close" className="tw-press text-tw-muted hover:text-white">
              <X size={15} strokeWidth={2.5} />
            </button>
          </div>
          <div className="border border-tw bg-white overflow-auto" style={{ maxHeight: 480 }}>
            <div dangerouslySetInnerHTML={{ __html: preview }} />
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Polish#11: "Zelflerend deze week" (numbers only, no content) --------
export function WeeklyLearningPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/weekly-learning");
        setData(data);
      } catch { /* fail-soft */ }
    })();
  }, []);
  if (!data) return null;
  return (
    <div className="mb-6 bg-tw-surface border border-tw p-4 sm:p-5" data-testid="weekly-learning-panel">
      <div className="flex items-center gap-2 mb-3">
        <GraduationCap size={14} className="text-tw-yellow" strokeWidth={2.5} />
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">ZELFLEREND DEZE WEEK</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MiniStat label="Nieuwe memo's" value={data.new_memos} testId="weekly-learning-memos" />
        <MiniStat label="Correcties" value={data.approved_corrections} testId="weekly-learning-corrections" />
        <MiniStat label="Nieuwe termen" value={data.promoted_vocab_terms} testId="weekly-learning-vocab" />
        <MiniStat label="AI-suggesties" value={data.ai_suggestions_generated} testId="weekly-learning-suggestions" />
        <MiniStat label="Beantwoorde vragen" value={data.questions_answered} testId="weekly-learning-answered" />
      </div>
    </div>
  );
}

export function AnalyticsAdmin() {
  const { colorOf } = useCategories();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/analytics");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) return <LoadingRow />;

  const { per_user, per_week, by_category, by_transform, logbook } = data;
  const maxWeek = Math.max(1, ...per_week.map((w) => w.count));
  const totals = {
    memos: per_user.reduce((a, u) => a + (u.memo_count || 0), 0),
    duration: per_user.reduce((a, u) => a + (u.total_duration_seconds || 0), 0),
    active7d: per_user.filter((u) => (u.memos_last_7d || 0) > 0).length,
    silent: per_user.filter(isSilent).length,
  };

  return (
    <div data-testid="admin-analytics-panel" className="space-y-6">
      {/* Top stat chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip label="TEAMLEDEN" value={per_user.length} />
        <StatChip label="TOTAAL MEMO'S" value={totals.memos} />
        <StatChip label="ACTIEF 7D" value={totals.active7d} accent="green" />
        <StatChip label="STIL" value={totals.silent} accent="muted" testId="analytics-silent-total" />
      </div>

      {/* Per-week activity */}
      <div className="bg-tw-surface border border-tw p-4 sm:p-5" data-testid="analytics-per-week">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={14} className="text-tw-yellow" strokeWidth={2.5} />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">
            ACTIVITEIT · LAATSTE 8 WEKEN
          </div>
        </div>
        <div className="flex items-end gap-2 h-32">
          {per_week.map((w, idx) => {
            const pct = Math.max(4, Math.round((w.count / maxWeek) * 100));
            const isCurrent = idx === per_week.length - 1;
            return (
              <div
                key={`${w.week_label}-${idx}`}
                className="flex-1 h-full flex flex-col items-center justify-end gap-1 min-w-0"
                data-testid={`analytics-week-bar-${idx}`}
              >
                <div className="text-[10px] font-mono text-tw-muted mb-0.5">
                  {w.count}
                </div>
                {/* Round O — the bar sits in a box that fills the rest of the row, so N% has a real height. */}
                <div className="w-full flex-1 min-h-0 flex items-end">
                  <div
                    className={`w-full transition-[height,background-color] duration-500 ${isCurrent ? "bg-tw-yellow" : "bg-tw-yellow/40"} hover:bg-tw-yellow`}
                    style={{ height: `${pct}%` }}
                    title={`${w.week_label}: ${w.count} memo's`}
                    data-testid={`analytics-week-bar-fill-${idx}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 mt-2">
          {per_week.map((w, idx) => (
            <div
              key={`lbl-${w.week_label}-${idx}`}
              className="flex-1 text-center text-[9px] font-mono uppercase tracking-widest text-tw-muted truncate"
            >
              {w.week_label}
            </div>
          ))}
        </div>
      </div>

      {/* Category + Transform chips */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-tw-surface border border-tw p-4" data-testid="analytics-by-category">
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted mb-3">
            CATEGORIEËN
          </div>
          {Object.keys(by_category).length === 0 ? (
            <div className="text-xs text-tw-muted">Nog geen data.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(by_category)
                .sort(([, a], [, b]) => b - a)
                .map(([cat, count]) => (
                  <span
                    key={cat}
                    data-testid={`analytics-cat-${cat}`}
                    className="inline-flex items-center gap-2 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-black"
                    style={{ backgroundColor: colorOf(cat) }}
                  >
                    {cat} <span className="font-mono">{count}</span>
                  </span>
                ))}
            </div>
          )}
        </div>
        <div className="bg-tw-surface border border-tw p-4" data-testid="analytics-by-transform">
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted mb-3">
            AI-VERWERKINGEN
          </div>
          {Object.keys(by_transform).length === 0 ? (
            <div className="text-xs text-tw-muted">Nog geen AI-verwerkingen.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(by_transform)
                .sort(([, a], [, b]) => b - a)
                .map(([t, count]) => (
                  <span
                    key={t}
                    data-testid={`analytics-transform-${t}`}
                    className="inline-flex items-center gap-2 border border-tw-yellow/60 text-tw-yellow px-2 py-1 font-mono text-[10px] tracking-[0.2em] uppercase"
                  >
                    <Sparkles size={10} strokeWidth={2.8} />
                    {TRANSFORM_LABELS[t] || t} <span>{count}</span>
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Per-user table */}
      <div data-testid="analytics-per-user">
        <div className="flex items-center gap-2 mb-3">
          <UsersIcon size={14} className="text-tw-yellow" strokeWidth={2.5} />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">
            PER TEAMLID
          </div>
        </div>
        <div className="bg-tw-surface border border-tw overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-tw text-left">
                <th className="px-4 py-3 font-mono text-[9px] tracking-[0.3em] uppercase text-tw-muted">Naam</th>
                <th className="px-4 py-3 font-mono text-[9px] tracking-[0.3em] uppercase text-tw-muted">Functie</th>
                <th className="px-4 py-3 font-mono text-[9px] tracking-[0.3em] uppercase text-tw-muted text-right">Memo&apos;s</th>
                <th className="px-4 py-3 font-mono text-[9px] tracking-[0.3em] uppercase text-tw-muted text-right">Totale duur</th>
                <th className="px-4 py-3 font-mono text-[9px] tracking-[0.3em] uppercase text-tw-muted">Laatste memo</th>
                <th className="px-4 py-3 font-mono text-[9px] tracking-[0.3em] uppercase text-tw-muted text-right">7 dagen</th>
              </tr>
            </thead>
            <tbody>
              {per_user.map((u) => {
                const silent = isSilent(u);
                return (
                  <tr
                    key={u.user_id}
                    data-testid={`analytics-user-row-${u.user_id}`}
                    className="border-b border-tw/60 last:border-b-0 hover:bg-tw-surface-hover transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white">{u.name}</span>
                        {silent && (
                          <span
                            data-testid={`analytics-user-silent-${u.user_id}`}
                            className="inline-flex items-center gap-1 border border-tw text-tw-muted px-1.5 py-0.5 font-mono text-[9px] tracking-[0.3em] uppercase"
                            title="Stil de laatste 14 dagen"
                          >
                            <MoonStar size={9} strokeWidth={2.8} /> Stil
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-tw-muted text-xs">
                      {u.functie || <em className="opacity-60">—</em>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-tw-yellow" data-testid={`analytics-user-count-${u.user_id}`}>
                      {u.memo_count}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white/80 text-xs">
                      {formatDurationSec(u.total_duration_seconds)}
                    </td>
                    <td className="px-4 py-3 text-tw-muted text-xs" data-testid={`analytics-user-last-${u.user_id}`}>
                      {formatRelativeNl(u.last_memo_at)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white/80" data-testid={`analytics-user-7d-${u.user_id}`}>
                      {u.memos_last_7d}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Logbook */}
      <div data-testid="analytics-logbook">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={14} className="text-tw-yellow" strokeWidth={2.5} />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">
            LOGBOEK · LAATSTE 50
          </div>
        </div>
        {logbook.length === 0 ? (
          <EmptyState label="Nog geen memo's" hint="Zodra het team spreekt, verschijnt het hier." testId="analytics-logbook-empty" />
        ) : (
          <div className="space-y-1.5">
            {logbook.map((r, i) => {
              const catColor = r.category ? colorOf(r.category) : null;
              return (
                <div
                  key={`${r.created_at}-${i}`}
                  data-testid={`analytics-logbook-row-${i}`}
                  className="bg-tw-surface border border-tw p-3 flex items-start gap-3 flex-wrap hover:border-tw-yellow transition-colors"
                >
                  <div className="font-mono text-[10px] tracking-widest text-tw-muted shrink-0 whitespace-nowrap">
                    {formatDate(r.created_at)}
                  </div>
                  <div className="text-white text-sm font-medium shrink-0">{r.user_name || "—"}</div>
                  {r.category && (
                    <span
                      className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-black shrink-0"
                      style={{ backgroundColor: catColor }}
                    >
                      {r.category}
                    </span>
                  )}
                  {r.transform_type && r.transform_type !== "raw" && (
                    <span className="inline-flex items-center gap-1 border border-tw-yellow/60 text-tw-yellow px-1.5 py-0.5 font-mono text-[9px] tracking-[0.2em] uppercase shrink-0">
                      <Sparkles size={9} strokeWidth={2.8} /> {TRANSFORM_LABELS[r.transform_type] || r.transform_type}
                    </span>
                  )}
                  <div className="text-xs text-tw-muted flex-1 min-w-[160px] truncate" title={r.question || undefined}>
                    {r.question || <em className="opacity-60">— geen vraag —</em>}
                  </div>
                  <div className="font-mono text-[10px] text-tw-muted shrink-0">
                    {formatDurationSec(r.duration_seconds)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Bibliotheek — reader visibility per category (R6) */}
      <LibrarySettingsCard />
      {/* Onderhoud — cleanup tools */}
      <OnderhoudCard />
    </div>
  );
}

function LibrarySettingsCard() {
  const { categories: categoryList, allCategories, names } = useCategories();
  const [cats, setCats] = useState([]); // enabled category names
  const [loading, setLoading] = useState(true);
  const [savingCat, setSavingCat] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/library-settings");
        setCats(data.reader_visible_categories || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (cat) => {
    const next = cats.includes(cat) ? cats.filter((c) => c !== cat) : [...cats, cat];
    setSavingCat(cat);
    try {
      const { data } = await api.put("/admin/library-settings", { reader_visible_categories: next });
      setCats(data.reader_visible_categories || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCat(null);
    }
  };

  // A category deactivated AFTER being switched on for lezers must stay visible + controllable
  // here (deactivate ≠ clear visibility) even though it's no longer in the active-only list.
  const inactiveButVisible = allCategories.filter((c) => !names.includes(c.name) && cats.includes(c.name));
  const rows = [...categoryList, ...inactiveButVisible];

  return (
    <div className="mt-6 bg-tw-surface border border-tw p-4 sm:p-5" data-testid="admin-library-settings">
      <div className="flex items-center gap-2 mb-1">
        <BookOpen size={14} className="text-tw-yellow" strokeWidth={2.5} />
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">BIBLIOTHEEK</div>
      </div>
      <p className="text-[11px] text-tw-muted mb-4 max-w-xl">
        Bepaal welke categorieën zichtbaar zijn voor lezers. Standaard is niets zichtbaar. Wijzigingen zijn direct actief.
      </p>
      {loading ? (
        <LoadingRow />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {rows.map((c) => {
            const cat = c.name;
            const on = cats.includes(cat);
            const isInactive = !names.includes(cat);
            return (
              <div
                key={cat}
                data-testid={`admin-library-row-${cat}`}
                className={`flex items-center justify-between gap-3 px-3 py-2.5 border transition-colors ${on ? "border-tw-yellow bg-tw-yellow/5" : "border-tw bg-tw-black"} ${isInactive ? "opacity-70" : ""}`}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className={`text-sm truncate ${on ? "text-white" : "text-tw-muted"}`}>{cat}</span>
                  {isInactive && (
                    <span className="font-mono text-[9px] uppercase tracking-widest text-tw-muted border border-tw px-1 py-0.5 shrink-0">(inactief)</span>
                  )}
                  <InfoPopover text={c.description} testId={`admin-library-info-${cat}`} />
                </span>
                <button
                  type="button"
                  onClick={() => toggle(cat)}
                  disabled={savingCat === cat}
                  data-testid={`admin-library-toggle-${cat}`}
                  aria-pressed={on}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${on ? "bg-tw-yellow" : "bg-tw"}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-black transition-transform ${on ? "translate-x-4" : "translate-x-1"}`} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function OnderhoudCard() {
  const [counts, setCounts] = useState({ inactive_questions: 0, revoked_keys: 0 });
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [purgeInput, setPurgeInput] = useState("");
  const [showDanger, setShowDanger] = useState(false);
  const [sttTesting, setSttTesting] = useState(false);
  const [sttResult, setSttResult] = useState(null); // { ok, transcript?, error? }
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null); // { enriched, skipped, failed }
  const [exportingFull, setExportingFull] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [qs, ks, sys] = await Promise.all([
        api.get("/admin/questions"),
        api.get("/admin/apikeys"),
        api.get("/admin/system"),
      ]);
      setCounts({
        inactive_questions: qs.data.filter((q) => !q.active).length,
        revoked_keys: ks.data.filter((k) => !k.active).length,
      });
      setSystem(sys.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const exportFull = async () => {
    setExportingFull(true);
    try {
      const res = await api.get("/admin/export/full", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename=([^;]+)/);
      a.download = (m && m[1].trim()) || "tw1-backup.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Volledige export gedownload.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExportingFull(false);
    }
  };

  const purgeInactive = async () => {
    if (counts.inactive_questions === 0) return;
    if (!window.confirm(`Verwijder ${counts.inactive_questions} inactieve vragen definitief?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/questions/purge-inactive");
      toast.success(`${data.removed} inactieve vragen verwijderd.`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const purgeRevokedKeys = async () => {
    if (counts.revoked_keys === 0) return;
    if (!window.confirm(`Verwijder ${counts.revoked_keys} ingetrokken sleutels definitief?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/apikeys/purge-revoked");
      toast.success(`${data.removed} sleutels verwijderd.`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runSttTest = async () => {
    setSttTesting(true);
    setSttResult(null);
    try {
      // Fetch the bundled Dutch sample audio and pipe it through the full /api/transcribe endpoint.
      const audioResp = await fetch("/samples/test-nl.mp3", { cache: "no-store" });
      if (!audioResp.ok) throw new Error(`Kon test-audio niet laden (HTTP ${audioResp.status}).`);
      const blob = await audioResp.blob();
      const form = new FormData();
      form.append("file", blob, "test-nl.mp3");
      const token = localStorage.getItem("tw1_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await fetch(`${base}/api/transcribe`, {
        method: "POST",
        headers: authHeaders,
        body: form,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const detail = body?.detail
          || (resp.status === 503 && "AI-sleutel ontbreekt of is ongeldig op de server.")
          || (resp.status === 504 && "Duurde te lang — probeer opnieuw.")
          || `HTTP ${resp.status}`;
        setSttResult({ ok: false, error: detail });
        toast.error(detail);
        return;
      }
      const start = await resp.json();
      const jobId = start?.job_id;
      if (!jobId) throw new Error("Geen taak-id ontvangen van de server.");
      // Poll the async job until done/error (max ~90s for the short sample).
      const deadline = Date.now() + 90_000;
      let transcript = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        if (Date.now() > deadline) throw new Error("Duurde te lang — probeer opnieuw.");
        const sResp = await fetch(`${base}/api/transcribe/${jobId}`, { headers: authHeaders });
        if (!sResp.ok) {
          if (sResp.status === 404) throw new Error("Transcriptie-taak niet gevonden.");
          continue;
        }
        const s = await sResp.json();
        if (s.status === "done") { transcript = (s.transcript || "").trim(); break; }
        if (s.status === "error") throw new Error(s.detail || "Test mislukt.");
      }
      setSttResult({ ok: true, transcript });
      toast.success("Transcriptie-pijplijn werkt.");
    } catch (e) {
      const msg = String(e?.message || e || "Test mislukt.");
      setSttResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setSttTesting(false);
    }
  };

  const runEnrich = async () => {
    if (!window.confirm("Alle memo's zonder titel verrijken met AI (titel, tags, samenvatting)? Dit kan even duren.")) return;
    setEnriching(true);
    setEnrichResult(null);
    const startedAt = Date.now();
    try {
      await api.post("/admin/enrich-recordings");
    } catch (e) {
      if (e?.response?.status === 409) {
        toast("Verrijking is al bezig — voortgang wordt geladen.");
      } else {
        toast.error(formatApiError(e));
        setEnriching(false);
        return;
      }
    }
    // Poll live progress until the background job reports done. A ~500ms minimum hold
    // avoids the progress state flashing on/off when a run finishes almost instantly.
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/enrich-recordings/status");
        setEnrichResult(data);
        if (data.running) {
          setTimeout(poll, 1500);
        } else {
          const wait = Math.max(0, 500 - (Date.now() - startedAt));
          setTimeout(() => {
            setEnriching(false);
            if (data.done) {
              toast.success(`${data.enriched} verrijkt · ${data.skipped} overgeslagen · ${data.failed} mislukt.`);
            }
          }, wait);
        }
      } catch (e) {
        setEnriching(false);
        toast.error(formatApiError(e));
      }
    };
    poll();
  };

  const purgeAllContent = async () => {    if (purgeInput !== "LEEG") return;
    if (!window.confirm(
      "LAATSTE WAARSCHUWING\n\nAlle memo's, alle feedback, de antwoord-cache en niet-afgewezen woordkandidaten worden definitief verwijderd. Afgewezen kandidaten, gebruikers, vragen, vocabulary, sleutels en instellingen blijven behouden.\n\nDoorgaan?"
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge-content", { confirm: "LEEG" });
      toast.success(
        `${data.removed_recordings} memo's en ${data.removed_feedback} feedback-items verwijderd.`
      );
      setPurgeInput("");
      setShowDanger(false);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="bg-tw-surface border border-tw p-4"><LoadingRow /></div>;
  }

  return (
    <div className="bg-tw-surface border border-tw p-4 sm:p-5" data-testid="onderhoud-card">
      <div className="flex items-center gap-2 mb-1">
        <Trash2 size={14} className="text-tw-yellow" strokeWidth={2.5} />
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">
          ONDERHOUD
        </div>
      </div>
      <p className="text-xs text-tw-muted mb-4 leading-relaxed">
        Ruim testdata op nu de app live is.
      </p>

      {/* Diagnostics — booleans only, no values ever exposed. */}
      {system && (
        <div className="bg-tw-black border border-tw p-3 mb-4" data-testid="onderhoud-system">
          <div className="font-mono text-[9px] tracking-[0.3em] text-tw-muted mb-2">
            SYSTEEM
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 font-mono text-[11px]">
            <DiagRow ok={system.llm_key_present} label="AI-sleutel" testId="diag-llm-key" />
            <DiagRow ok={system.feedback_hub_key_present} label="Feedback-hub" testId="diag-feedback-hub" />
            <DiagRow ok={system.ffmpeg_ok} label="ffmpeg" testId="diag-ffmpeg" />
            <span className="text-tw-muted">
              Whisper-model:{" "}
              <span className="text-white" data-testid="diag-whisper-model">
                {system.whisper_model || "—"}
              </span>
            </span>
          </div>
          {system.ffmpeg_ok && system.ffmpeg_path && (
            <div className="mt-1.5 font-mono text-[10px] text-tw-muted truncate" data-testid="diag-ffmpeg-path">
              pad: {system.ffmpeg_path}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              onClick={runSttTest}
              disabled={sttTesting || !system.llm_key_present}
              data-testid="onderhoud-stt-test-btn"
              className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-3 disabled:opacity-40"
            >
              {sttTesting ? (
                <><Loader2 size={12} className="animate-spin mr-1" /> Bezig…</>
              ) : (
                <>Test transcriptie</>
              )}
            </Button>
            <span className="font-mono text-[10px] text-tw-muted">
              Stuurt een korte Nederlandse audio door de /api/transcribe pijplijn.
            </span>
          </div>
          {sttResult && (
            <div
              data-testid={sttResult.ok ? "onderhoud-stt-ok" : "onderhoud-stt-error"}
              className={`mt-3 p-3 text-xs leading-relaxed border ${
                sttResult.ok
                  ? "border-[#32D74B]/50 bg-[#32D74B]/5 text-[#32D74B]"
                  : "border-tw-red/60 bg-tw-red/5 text-tw-red"
              }`}
            >
              {sttResult.ok ? (
                <>
                  <div className="font-mono text-[9px] tracking-[0.3em] uppercase mb-1">Transcriptie</div>
                  <div className="text-white italic" data-testid="onderhoud-stt-transcript">
                    &ldquo;{sttResult.transcript || "(leeg — spraakloos of stil)"}&rdquo;
                  </div>
                </>
              ) : (
                <>
                  <div className="font-mono text-[9px] tracking-[0.3em] uppercase mb-1">Fout</div>
                  <div data-testid="onderhoud-stt-error-text">{sttResult.error}</div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* R1 — Verrijk bestaande memo's (AI backfill) */}
      <div className="bg-tw-black border border-tw p-3 mb-4" data-testid="onderhoud-enrich">
        <div className="font-mono text-[9px] tracking-[0.3em] text-tw-muted mb-2">
          VERRIJKING
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={runEnrich}
            disabled={enriching || !system?.llm_key_present}
            data-testid="onderhoud-enrich-btn"
            className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-3 disabled:opacity-40"
          >
            {enriching ? (
              <><Loader2 size={12} className="animate-spin mr-1" /> Bezig…</>
            ) : (
              <><Sparkles size={12} strokeWidth={2.5} className="mr-1" /> Verrijk bestaande memo's</>
            )}
          </Button>
          <span className="font-mono text-[10px] text-tw-muted">
            Voegt AI-titel, tags en samenvatting toe aan memo's die dat nog missen.
          </span>
        </div>
        {enrichResult && (
          <div
            data-testid="onderhoud-enrich-result"
            className="mt-3 p-3 text-xs leading-relaxed border border-[#32D74B]/50 bg-[#32D74B]/5 text-white"
          >
            {enrichResult.running && (
              <div className="font-mono text-[10px] text-tw-yellow mb-1" data-testid="onderhoud-enrich-progress">
                Bezig… {enrichResult.processed || 0}/{enrichResult.todo || 0} verwerkt
              </div>
            )}
            <span className="text-[#32D74B] font-mono">{enrichResult.enriched || 0}</span> verrijkt ·{" "}
            <span className="text-tw-muted font-mono">{enrichResult.skipped || 0}</span> overgeslagen ·{" "}
            <span className="text-tw-red font-mono">{enrichResult.failed || 0}</span> mislukt
          </div>
        )}
      </div>

      {/* F1 — Volledige back-up (admin JWT only) */}
      <div className="bg-tw-black border border-tw p-3 mb-4" data-testid="onderhoud-backup">
        <div className="font-mono text-[9px] tracking-[0.3em] text-tw-muted mb-2">
          BACK-UP
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={exportFull}
            disabled={exportingFull}
            data-testid="onderhoud-export-full-btn"
            className="h-9 rounded-none bg-tw-yellow text-black hover:bg-[#E6C200] uppercase tracking-[0.2em] text-[10px] font-bold px-3 disabled:opacity-40"
          >
            {exportingFull ? (
              <><Loader2 size={12} className="animate-spin mr-1" /> Bezig…</>
            ) : (
              <><Download size={12} strokeWidth={2.5} className="mr-1" /> Alles exporteren</>
            )}
          </Button>
          <span className="font-mono text-[10px] text-tw-muted">
            Downloadt één zip met alle gegevens (zonder wachtwoorden of sleutels).
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={purgeInactive}
          disabled={busy || counts.inactive_questions === 0}
          data-testid="onderhoud-purge-inactive-questions"
          className="tw-press bg-tw-black border border-tw text-white hover:border-tw-yellow disabled:opacity-40 disabled:hover:border-tw p-3 text-left transition-colors"
        >
          <div className="font-mono text-[9px] tracking-[0.3em] text-tw-muted mb-1">
            INACTIEVE VRAGEN
          </div>
          <div className="text-sm text-white leading-snug">
            Verwijder alle inactieve vragen{" "}
            <span className="text-tw-yellow font-mono" data-testid="onderhoud-inactive-questions-count">
              ({counts.inactive_questions})
            </span>
          </div>
        </button>

        <button
          type="button"
          onClick={purgeRevokedKeys}
          disabled={busy || counts.revoked_keys === 0}
          data-testid="onderhoud-purge-revoked-keys"
          className="tw-press bg-tw-black border border-tw text-white hover:border-tw-yellow disabled:opacity-40 disabled:hover:border-tw p-3 text-left transition-colors"
        >
          <div className="font-mono text-[9px] tracking-[0.3em] text-tw-muted mb-1">
            INGETROKKEN SLEUTELS
          </div>
          <div className="text-sm text-white leading-snug">
            Verwijder alle ingetrokken sleutels{" "}
            <span className="text-tw-yellow font-mono" data-testid="onderhoud-revoked-keys-count">
              ({counts.revoked_keys})
            </span>
          </div>
        </button>
      </div>

      {/* Danger zone — visually separated */}
      <div className="mt-5 pt-5 border-t border-tw-red/40">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={13} className="text-tw-red" strokeWidth={2.5} />
          <div className="font-mono text-[10px] tracking-[0.3em] text-tw-red uppercase">
            Gevaarlijk
          </div>
        </div>
        {!showDanger ? (
          <button
            type="button"
            onClick={() => setShowDanger(true)}
            data-testid="onderhoud-open-danger"
            className="tw-press text-left w-full border border-tw-red/60 hover:border-tw-red text-tw-red hover:bg-tw-red/10 p-3 transition-colors"
          >
            <div className="text-sm leading-snug">
              Maak de kluis leeg — verwijder alle memo&apos;s, feedback, antwoord-cache en woordkandidaten
            </div>
            <div className="mt-1 font-mono text-[10px] text-tw-muted">
              Afgewezen kandidaten, gebruikers, vragen, vocabulary, sleutels en instellingen blijven behouden.
            </div>
          </button>
        ) : (
          <div className="border border-tw-red p-3 bg-tw-red/5" data-testid="onderhoud-danger-panel">
            <p className="text-sm text-white mb-3 leading-relaxed">
              Typ <span className="font-mono font-bold text-tw-red">LEEG</span> om te bevestigen. Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Input
                value={purgeInput}
                onChange={(e) => setPurgeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && purgeInput === "LEEG") purgeAllContent(); }}
                placeholder="LEEG"
                data-testid="onderhoud-purge-confirm-input"
                className="bg-tw-black border-tw text-white h-10 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-red flex-1 min-w-[140px] font-mono"
              />
              <Button
                onClick={purgeAllContent}
                disabled={busy || purgeInput !== "LEEG"}
                data-testid="onderhoud-purge-content-btn"
                className="h-10 rounded-none bg-tw-red text-white hover:bg-tw-red/90 uppercase tracking-[0.2em] text-[10px] font-bold disabled:opacity-40 px-4"
              >
                {busy ? <Loader2 className="animate-spin" size={14} /> : "Verwijder alles"}
              </Button>
              <Button
                onClick={() => { setShowDanger(false); setPurgeInput(""); }}
                variant="outline"
                className="h-10 rounded-none bg-transparent border-tw text-white hover:bg-tw-surface-hover hover:text-white uppercase tracking-[0.2em] text-[10px]"
              >
                Annuleer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
